import React, { useState, useEffect } from 'react';
import { Calendar, Clock, Mail, Phone, Building, FileText, ExternalLink, Eye, CheckCircle, XCircle, Clock as ClockIcon, User } from 'lucide-react';

const ConsultationRequestsList = () => {
  const [consultations, setConsultations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedConsultation, setSelectedConsultation] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    loadConsultations();
  }, []);

  const loadConsultations = async () => {
    try {
      setIsLoading(true);
      setError('');
      
      const response = await fetch('/api/consultation.php?action=get_all');
      const result = await response.json();
      
      if (result.success) {
        setConsultations(result.data || []);
      } else {
        setError('Failed to load consultations: ' + (result.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error loading consultations:', err);
      setError('Failed to connect to API. Please check your server configuration.');
    } finally {
      setIsLoading(false);
    }
  };

  const updateConsultationStatus = async (id, newStatus, actualDateTime = null) => {
    try {
      const response = await fetch('/api/consultation.php?action=update_status', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: id,
          status: newStatus,
          actual_datetime: actualDateTime
        })
      });

      const result = await response.json();

      if (result.success) {
        setConsultations(prev => 
          prev.map(cons => 
            cons.id === id ? { ...cons, status: newStatus, actual_datetime: actualDateTime } : cons
          )
        );
        if (selectedConsultation && selectedConsultation.id === id) {
          setSelectedConsultation({ ...selectedConsultation, status: newStatus, actual_datetime: actualDateTime });
        }
      } else {
        alert('Error updating status: ' + result.error);
      }
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Failed to update status');
    }
  };

  const updateConsultationNotes = async (id, notes) => {
    try {
      const response = await fetch('/api/consultation.php?action=update_notes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: id,
          notes: notes
        })
      });

      const result = await response.json();

      if (result.success) {
        setConsultations(prev => 
          prev.map(cons => 
            cons.id === id ? { ...cons, notes: notes } : cons
          )
        );
        if (selectedConsultation && selectedConsultation.id === id) {
          setSelectedConsultation({ ...selectedConsultation, notes: notes });
        }
      } else {
        alert('Error updating notes: ' + result.error);
      }
    } catch (err) {
      console.error('Error updating notes:', err);
      alert('Failed to update notes');
    }
  };

  const handleViewDetails = (consultation) => {
    setSelectedConsultation(consultation);
    setShowModal(true);
  };

  const filteredConsultations = consultations.filter(cons => {
    if (statusFilter === 'all') return true;
    return cons.status === statusFilter;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'confirmed': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <ClockIcon className="w-4 h-4" />;
      case 'confirmed': return <CheckCircle className="w-4 h-4" />;
      case 'completed': return <CheckCircle className="w-4 h-4" />;
      case 'cancelled': return <XCircle className="w-4 h-4" />;
      default: return <ClockIcon className="w-4 h-4" />;
    }
  };

  const getConsultationTypeDisplay = (type) => {
    switch (type) {
      case 'strategy': return 'Strategy Session (45 min)';
      case 'technical': return 'Technical Review (30 min)';
      case 'discovery': return 'Discovery Call (60 min)';
      default: return type;
    }
  };

  const getTimeSlotDisplay = (timeSlot) => {
    switch (timeSlot) {
      case 'morning': return 'Morning (09:00-11:30)';
      case 'midday': return 'Midday (12:00-14:30)';
      case 'afternoon': return 'Afternoon (15:00-17:30)';
      case 'evening': return 'Evening (18:00-19:30)';
      default: return timeSlot;
    }
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return 'N/A';
    }
  };

  const formatDateTime = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'N/A';
    }
  };

  const parseDocuments = (documentsJson) => {
    try {
      return documentsJson ? JSON.parse(documentsJson) : [];
    } catch {
      return [];
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        {/* <div>
          <h2 className="text-2xl font-bold text-gray-900">Consultation Requests</h2>
          <p className="text-gray-600">Manage consultation bookings and schedule appointments</p>
        </div> */}
        <div className="flex items-center space-x-4">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <button
            onClick={loadConsultations}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <XCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Consultations List */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Client
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Consultation
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Schedule
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredConsultations.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                    No consultations found
                  </td>
                </tr>
              ) : (
                filteredConsultations.map((consultation) => (
                  <tr key={consultation.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {consultation.name}
                          </div>
                          <div className="text-sm text-gray-500 flex items-center">
                            <Mail className="w-4 h-4 mr-1" />
                            {consultation.email}
                          </div>
                          {consultation.phone && (
                            <div className="text-sm text-gray-500 flex items-center">
                              <Phone className="w-4 h-4 mr-1" />
                              {consultation.phone}
                            </div>
                          )}
                          {consultation.company && (
                            <div className="text-sm text-gray-500 flex items-center">
                              <Building className="w-4 h-4 mr-1" />
                              {consultation.company}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        <div className="font-medium mb-1">
                          {getConsultationTypeDisplay(consultation.consultation_type)}
                        </div>
                        {consultation.project_type && (
                          <div className="text-xs text-gray-500">
                            Project: {consultation.project_type}
                          </div>
                        )}
                        {parseDocuments(consultation.documents).length > 0 && (
                          <div className="mt-2 flex items-center text-xs text-blue-600">
                            <FileText className="w-3 h-3 mr-1" />
                            {parseDocuments(consultation.documents).length} file(s)
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        <div className="flex items-center mb-1">
                          <Calendar className="w-4 h-4 mr-1" />
                          {formatDate(consultation.preferred_date)}
                        </div>
                        <div className="flex items-center text-xs text-gray-500">
                          <Clock className="w-3 h-3 mr-1" />
                          {getTimeSlotDisplay(consultation.preferred_time)}
                        </div>
                        {consultation.actual_datetime && (
                          <div className="text-xs text-green-600 mt-1">
                            Actual: {formatDateTime(consultation.actual_datetime)}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(consultation.status)}`}>
                        {getStatusIcon(consultation.status)}
                        <span className="ml-1 capitalize">{consultation.status}</span>
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleViewDetails(consultation)}
                          className="text-blue-600 hover:text-blue-900 flex items-center"
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </button>
                        <select
                          value={consultation.status}
                          onChange={(e) => updateConsultationStatus(consultation.id, e.target.value)}
                          className="border border-gray-300 rounded px-2 py-1 text-xs"
                        >
                          <option value="pending">Pending</option>
                          <option value="confirmed">Confirmed</option>
                          <option value="completed">Completed</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Details Modal */}
      {showModal && selectedConsultation && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-start mb-6">
                <h3 className="text-lg font-medium text-gray-900">
                  Consultation Details
                </h3>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Client Name
                    </label>
                    <p className="text-sm text-gray-900">{selectedConsultation.name}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <p className="text-sm text-gray-900">
                      <a href={`mailto:${selectedConsultation.email}`} className="text-blue-600 hover:text-blue-800">
                        {selectedConsultation.email}
                      </a>
                    </p>
                  </div>
                  {selectedConsultation.phone && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phone
                      </label>
                      <p className="text-sm text-gray-900">
                        <a href={`tel:${selectedConsultation.phone}`} className="text-blue-600 hover:text-blue-800">
                          {selectedConsultation.phone}
                        </a>
                      </p>
                    </div>
                  )}
                  {selectedConsultation.company && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Company
                      </label>
                      <p className="text-sm text-gray-900">{selectedConsultation.company}</p>
                    </div>
                  )}
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Consultation Type
                    </label>
                    <p className="text-sm text-gray-900">{getConsultationTypeDisplay(selectedConsultation.consultation_type)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Preferred Date
                    </label>
                    <p className="text-sm text-gray-900">{formatDate(selectedConsultation.preferred_date)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Preferred Time
                    </label>
                    <p className="text-sm text-gray-900">{getTimeSlotDisplay(selectedConsultation.preferred_time)}</p>
                  </div>
                  {selectedConsultation.project_type && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Project Type
                      </label>
                      <p className="text-sm text-gray-900">{selectedConsultation.project_type}</p>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <select
                      value={selectedConsultation.status}
                      onChange={(e) => updateConsultationStatus(selectedConsultation.id, e.target.value)}
                      className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="pending">Pending</option>
                      <option value="confirmed">Confirmed</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                </div>
              </div>

              {selectedConsultation.goals && (
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Goals & Objectives
                  </label>
                  <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
                    <p className="text-sm text-gray-900 whitespace-pre-wrap">{selectedConsultation.goals}</p>
                  </div>
                </div>
              )}

              {parseDocuments(selectedConsultation.documents).length > 0 && (
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Attached Files
                  </label>
                  <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
                    {parseDocuments(selectedConsultation.documents).map((doc, index) => (
                      <div key={index} className="flex items-center justify-between py-2 border-b border-gray-200 last:border-b-0">
                        <div className="flex items-center">
                          <FileText className="w-4 h-4 mr-2 text-gray-500" />
                          <span className="text-sm text-gray-900">{doc.original_name}</span>
                          <span className="text-xs text-gray-500 ml-2">
                            ({(doc.size / 1024 / 1024).toFixed(2)} MB)
                          </span>
                        </div>
                        <a
                          href={`/uploads/${doc.stored_name}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 flex items-center"
                        >
                          <ExternalLink className="w-4 h-4 mr-1" />
                          Download
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  value={selectedConsultation.notes || ''}
                  onChange={(e) => updateConsultationNotes(selectedConsultation.id, e.target.value)}
                  rows={4}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Add notes about this consultation..."
                />
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowModal(false)}
                  className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md text-sm font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConsultationRequestsList;